import React, { ReactNode } from 'react';
import { Button, ButtonProps, Tooltip } from 'antd';
import { usePermissionsContext } from '../context/permissions-context';
import { localizeByKeyStatic } from 'components/service';

interface PermissionButtonProps extends ButtonProps {
  permission: string | { slug: string; entryPoint?: string; groupName?: string; action?: string };
  disabledFallback?: boolean;
  children: ReactNode;
}

interface AnyPermissionButtonProps extends ButtonProps {
  permissions: (string | { slug: string; entryPoint?: string; groupName?: string; action?: string })[];
  disabledFallback?: boolean;
  children: ReactNode;
}

interface AllPermissionsButtonProps extends ButtonProps {
  permissions: (string | { slug: string; entryPoint?: string; groupName?: string; action?: string })[];
  disabledFallback?: boolean;
  children: ReactNode;
}

export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  disabledFallback = false,
  children,
  onClick,
  disabled,
  ...buttonProps
}) => {
  const { hasPermission, showPermissionDenied } = usePermissionsContext();

  const hasAccess = typeof permission === 'string'
    ? hasPermission(permission)
    : hasPermission(permission.slug, permission.entryPoint, permission.groupName, permission.action);

  const isDisabled = disabled || (!hasAccess && disabledFallback);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasAccess) {
      const permissionSlug = typeof permission === 'string' ? permission : permission.slug;
      showPermissionDenied(permissionSlug);
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  // Tooltip for disabled button
  const tooltipTitle = !hasAccess && isDisabled
    ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(typeof permission === 'string' ? permission : permission.slug)}`
    : undefined;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...buttonProps}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};

export const AnyPermissionButton: React.FC<AnyPermissionButtonProps> = ({
  permissions,
  disabledFallback = false,
  children,
  onClick,
  disabled,
  ...buttonProps
}) => {
  const { hasAnyPermission, showPermissionDenied } = usePermissionsContext();

  const permissionSlugs = permissions.map(p => typeof p === 'string' ? p : p.slug);
  const hasAccess = hasAnyPermission(permissionSlugs);

  const isDisabled = disabled || (!hasAccess && disabledFallback);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' OR '));
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  // Tooltip for disabled button
  const tooltipTitle = !hasAccess && isDisabled
    ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${permissionSlugs.map(slug => localizeByKeyStatic(slug)).join(' OR ')}`
    : undefined;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...buttonProps}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};

export const AllPermissionsButton: React.FC<AllPermissionsButtonProps> = ({
  permissions,
  disabledFallback = false,
  children,
  onClick,
  disabled,
  ...buttonProps
}) => {
  const { hasAllPermissions, showPermissionDenied } = usePermissionsContext();

  const permissionSlugs = permissions.map(p => typeof p === 'string' ? p : p.slug);
  const hasAccess = hasAllPermissions(permissionSlugs);

  const isDisabled = disabled || (!hasAccess && disabledFallback);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasAccess) {
      showPermissionDenied(permissionSlugs.join(' AND '));
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  // Tooltip for disabled button
  const tooltipTitle = !hasAccess && isDisabled
    ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${permissionSlugs.map(slug => localizeByKeyStatic(slug)).join(' AND ')}`
    : undefined;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...buttonProps}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};

// Button component for checking permission by slug dynamically
export const PermissionButtonBySlug: React.FC<ButtonProps & {
  permissionSlug: string;
  disabledFallback?: boolean;
  children: ReactNode;
}> = ({
  permissionSlug,
  disabledFallback = false,
  children,
  onClick,
  disabled,
  ...buttonProps
}) => {
  const { permissions, hasPermission, showPermissionDenied } = usePermissionsContext();

  // Find permission by slug
  const permission = permissions.find(p => p.slug === permissionSlug);

  const hasAccess = permission ? hasPermission(
    permission.slug,
    permission.entry_point,
    permission.group_name,
    permission.action
  ) : false;

  const isDisabled = disabled || (!hasAccess && disabledFallback);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasAccess) {
      showPermissionDenied(permissionSlug);
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  // Tooltip for disabled button
  const tooltipTitle = !hasAccess && isDisabled
    ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(permissionSlug)}`
    : undefined;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...buttonProps}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};
